/**
 * manchala-i18n.js — vanilla JS i18n + format loader (UMD-ish)
 *
 * Zero dependencies. Reads from MANCHALA licensor translation registry.
 * Caches dictionaries + locale formats in localStorage. Auto-binds elements
 * with data-i18n / data-i18n-control. Supports control-id addressing so
 * any form's caption, input, button etc. can be looked up by precise address.
 *
 *   ManchalaI18n.init({ apiBase, project, defaultLang, cacheTTLms, onChange })
 *
 *   // Lookups
 *   ManchalaI18n.t(key, fallback?)
 *   ManchalaI18n.tc(controlId, fallback?)
 *   ManchalaI18n.tx({ control_id, screen_name, project_name }, fallback?)
 *
 *   // Locale
 *   ManchalaI18n.setLang(code)
 *   ManchalaI18n.currentLang()
 *   ManchalaI18n.locales()
 *   ManchalaI18n.refresh()
 *
 *   // Formatting (per-locale currency / number / date / time)
 *   ManchalaI18n.formatCurrency(amount, opts?)
 *   ManchalaI18n.formatNumber(n, opts?)
 *   ManchalaI18n.formatDate(d, fmt?)
 *   ManchalaI18n.format()    → raw config for the active locale
 *
 * Auto-binding (no JS required for most pages):
 *   <h1 data-i18n="signup.headline">…fallback…</h1>
 *   <button data-i18n-control="btnSubmit">…fallback…</button>
 *   <span data-i18n-currency>1234.5</span>          → ₹1,234.50 (or €1.234,50)
 *   <input data-i18n-placeholder="signup.email_placeholder" />
 *
 * Generated by the MANCHALA licensor — do not edit by hand.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.ManchalaI18n = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var state = {
    apiBase:  '',
    project:  null,                // optional — passed to /api/i18n/dict?project=
    lang:     'en-IN',
    dict:     {},
    flat:     {},
    byControl:{},
    locales:  [],
    formats:  {},                  // { 'en-IN': { currency_symbol, … } }
    ttl:      60 * 60 * 1000,
    onChange: null,
    ready:    false,
  };

  function dkey(lang)  { return 'manchala-i18n:' + lang; }
  function fkey()      { return 'manchala-i18n:formats'; }

  function readCache(k) {
    try {
      var raw = localStorage.getItem(k);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || (Date.now() - parsed.fetchedAt) > state.ttl) return null;
      return parsed;
    } catch (e) { return null; }
  }
  function writeCache(k, payload) {
    try { localStorage.setItem(k, JSON.stringify(Object.assign({ fetchedAt: Date.now() }, payload))); }
    catch (e) { /* quota — ignore */ }
  }

  async function fetchLocales() {
    try {
      var r = await fetch(state.apiBase + '/api/i18n/locales');
      var d = await r.json();
      state.locales = d.locales || [];
    } catch (e) {
      state.locales = [
        // India — primary
        { code: 'en-IN', label: 'English',    rtl: false, group: 'india' },
        { code: 'hi-IN', label: 'हिन्दी',       rtl: false, group: 'india' },
        { code: 'te-IN', label: 'తెలుగు',       rtl: false, group: 'india' },
        { code: 'ta-IN', label: 'தமிழ்',        rtl: false, group: 'india' },
        { code: 'kn-IN', label: 'ಕನ್ನಡ',       rtl: false, group: 'india' },
        // India — others
        { code: 'mr-IN', label: 'मराठी',       rtl: false, group: 'india-other' },
        { code: 'bn-IN', label: 'বাংলা',       rtl: false, group: 'india-other' },
        { code: 'gu-IN', label: 'ગુજરાતી',      rtl: false, group: 'india-other' },
        { code: 'ml-IN', label: 'മലയാളം',    rtl: false, group: 'india-other' },
        { code: 'pa-IN', label: 'ਪੰਜਾਬੀ',        rtl: false, group: 'india-other' },
        { code: 'or-IN', label: 'ଓଡ଼ିଆ',        rtl: false, group: 'india-other' },
        { code: 'ur-IN', label: 'اردو',        rtl: true,  group: 'india-other' },
        // International — top 5
        { code: 'ar-AE', label: 'العربية',      rtl: true,  group: 'international' },
        { code: 'fr-FR', label: 'Français',   rtl: false, group: 'international' },
        { code: 'es-ES', label: 'Español',    rtl: false, group: 'international' },
        { code: 'de-DE', label: 'Deutsch',    rtl: false, group: 'international' },
        { code: 'it-IT', label: 'Italiano',   rtl: false, group: 'international' },
        { code: 'el-GR', label: 'Ελληνικά',    rtl: false, group: 'international-other' },
      ];
    }
    return state.locales;
  }

  async function fetchFormats() {
    var cached = readCache(fkey());
    if (cached) { state.formats = cached.formats || {}; return; }
    try {
      var r = await fetch(state.apiBase + '/api/i18n/formats');
      var d = await r.json();
      var map = {};
      (d.formats || []).forEach(function (f) { map[f.lang_code] = f; });
      state.formats = map;
      writeCache(fkey(), { formats: map });
    } catch (e) {
      // Conservative fallback — Indian formatting.
      state.formats = { 'en-IN': { currency_code: 'INR', currency_symbol: '₹', currency_position: 'prefix', thousands_sep: ',', decimal_sep: '.', decimal_places: 2, date_format: 'DD/MM/YYYY', time_format: 'HH:mm', first_day_of_week: 1 } };
    }
  }

  async function fetchDict(lang) {
    var cached = readCache(dkey(lang));
    if (cached) {
      state.dict = cached.dict || {};
      state.flat = cached.flat || {};
      state.byControl = cached.byControl || {};
      return;
    }
    try {
      var url = state.apiBase + '/api/i18n/dict?lang=' + encodeURIComponent(lang);
      if (state.project) url += '&project=' + encodeURIComponent(state.project);
      var r = await fetch(url);
      var d = await r.json();
      state.dict = d.nested || {};
      state.flat = d.flat   || {};
      state.byControl = d.byControl || {};
      writeCache(dkey(lang), { dict: state.dict, flat: state.flat, byControl: state.byControl });
    } catch (e) {
      state.dict = {}; state.flat = {}; state.byControl = {};
    }
  }

  function applyDirection() {
    if (typeof document === 'undefined') return;
    var loc = state.locales.find(function (l) { return l.code === state.lang; });
    document.documentElement.setAttribute('dir',  loc && loc.rtl ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', state.lang);
  }

  function rebindDom() {
    if (typeof document === 'undefined') return;
    document.querySelectorAll('[data-i18n]').forEach(function (el) {
      var key = el.getAttribute('data-i18n');
      var fb  = el.getAttribute('data-i18n-fallback') || el.textContent;
      el.textContent = t(key, fb);
    });
    document.querySelectorAll('[data-i18n-control]').forEach(function (el) {
      var cid = el.getAttribute('data-i18n-control');
      var fb  = el.getAttribute('data-i18n-fallback') || el.textContent;
      el.textContent = tc(cid, fb);
    });
    document.querySelectorAll('[data-i18n-placeholder]').forEach(function (el) {
      el.setAttribute('placeholder', t(el.getAttribute('data-i18n-placeholder'), el.getAttribute('placeholder') || ''));
    });
    document.querySelectorAll('[data-i18n-title]').forEach(function (el) {
      el.setAttribute('title', t(el.getAttribute('data-i18n-title'), el.getAttribute('title') || ''));
    });
    document.querySelectorAll('[data-i18n-currency]').forEach(function (el) {
      var n = parseFloat(el.getAttribute('data-i18n-currency') || el.textContent);
      if (!isNaN(n)) el.textContent = formatCurrency(n);
    });
    document.querySelectorAll('[data-i18n-number]').forEach(function (el) {
      var n = parseFloat(el.getAttribute('data-i18n-number') || el.textContent);
      if (!isNaN(n)) el.textContent = formatNumber(n);
    });
  }

  // ── Lookups ────────────────────────────────────────────────────────────────
  function t(key, fallback) {
    if (!key) return fallback || '';
    if (state.flat[key] != null) return state.flat[key];
    var parts = String(key).split('.');
    var node  = state.dict;
    for (var i = 0; i < parts.length; i++) {
      if (node && typeof node === 'object' && parts[i] in node) node = node[parts[i]];
      else return fallback != null ? fallback : key;
    }
    return (typeof node === 'string') ? node : (fallback != null ? fallback : key);
  }

  // Direct control_id lookup — instant, no API roundtrip if dict was prefetched.
  function tc(controlId, fallback) {
    if (!controlId) return fallback || '';
    if (state.byControl[controlId] != null) return state.byControl[controlId];
    return fallback != null ? fallback : controlId;
  }

  // Full-address async lookup (use only if not pre-fetched).
  async function tx(addr, fallback) {
    if (!addr || !addr.control_id) return fallback || '';
    var hit = state.byControl[addr.control_id];
    if (hit != null) return hit;
    try {
      var q = new URLSearchParams({
        control_id: addr.control_id, lang: state.lang,
        screen:  addr.screen_name  || '',
        project: addr.project_name || state.project || '',
      });
      var r = await fetch(state.apiBase + '/api/i18n/by-control?' + q.toString());
      var d = await r.json();
      return d.found ? d.value : (fallback != null ? fallback : addr.control_id);
    } catch (e) { return fallback != null ? fallback : addr.control_id; }
  }

  // ── Format helpers (centrally configured per locale) ──────────────────────
  function format() { return state.formats[state.lang] || state.formats['en-IN'] || {}; }

  function groupThousands(intPart, sep) {
    // Western: every 3. Indian (INR): first 3 from right, then every 2.
    var f = format();
    if (f.currency_code === 'INR') {
      var s = String(intPart);
      if (s.length <= 3) return s;
      var lastThree = s.slice(-3);
      var rest      = s.slice(0, -3);
      rest = rest.replace(/\B(?=(\d{2})+(?!\d))/g, sep);
      return rest + sep + lastThree;
    }
    return String(intPart).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  function formatNumber(n, opts) {
    opts = opts || {};
    var f  = format();
    if (n == null || isNaN(n)) return '';
    var dp = opts.decimals != null ? opts.decimals : f.decimal_places;
    var sign = n < 0 ? '-' : '';
    var abs  = Math.abs(Number(n));
    var fixed = abs.toFixed(dp);
    var parts = fixed.split('.');
    var intStr = groupThousands(parts[0], f.thousands_sep || ',');
    return sign + intStr + (parts[1] ? (f.decimal_sep || '.') + parts[1] : '');
  }

  function formatCurrency(amount, opts) {
    var f = format();
    var num = formatNumber(amount, opts);
    var sym = (opts && opts.symbol) || f.currency_symbol || '';
    if (f.currency_position === 'suffix') return num + ' ' + sym;
    return sym + num;
  }

  function formatDate(d, fmt) {
    if (!d) return '';
    var date = (d instanceof Date) ? d : new Date(d);
    if (isNaN(date.getTime())) return '';
    var f = format();
    var pattern = fmt || f.date_format || 'DD/MM/YYYY';
    var dd = String(date.getDate()).padStart(2, '0');
    var mm = String(date.getMonth() + 1).padStart(2, '0');
    var yy = String(date.getFullYear());
    return pattern.replace('DD', dd).replace('MM', mm).replace('YYYY', yy).replace('YY', yy.slice(-2));
  }

  // ── Public API ─────────────────────────────────────────────────────────────
  async function init(opts) {
    opts = opts || {};
    state.apiBase  = opts.apiBase     || state.apiBase || '';
    state.project  = opts.project     || state.project || null;
    state.lang     = opts.defaultLang || localStorage.getItem('manchala-i18n:lang') || 'en-IN';
    state.ttl      = opts.cacheTTLms  || state.ttl;
    state.onChange = typeof opts.onChange === 'function' ? opts.onChange : null;
    await fetchLocales();
    await Promise.all([fetchDict(state.lang), fetchFormats()]);
    state.ready = true;
    applyDirection();
    rebindDom();
    if (state.onChange) state.onChange(state.lang);
  }

  async function setLang(code) {
    if (!code || code === state.lang) return;
    state.lang = code;
    try { localStorage.setItem('manchala-i18n:lang', code); } catch (e) {}
    applyDirection();           // flip DIR immediately — race-free across rapid toggles
    await fetchDict(code);
    rebindDom();
    if (state.onChange) state.onChange(state.lang);
  }

  async function refresh() {
    try {
      localStorage.removeItem(dkey(state.lang));
      localStorage.removeItem(fkey());
    } catch (e) {}
    await Promise.all([fetchDict(state.lang), fetchFormats()]);
    rebindDom();
  }

  function currentLang() { return state.lang; }
  function locales()     { return state.locales.slice(); }
  function isReady()     { return state.ready; }

  return {
    init: init,
    t: t, tc: tc, tx: tx,
    setLang: setLang, currentLang: currentLang, locales: locales, refresh: refresh, isReady: isReady,
    formatNumber: formatNumber, formatCurrency: formatCurrency, formatDate: formatDate, format: format,
  };
}));
